/**
 * Stepper — progress indicator for multi-step wizards.
 *
 * Generic over step id type. Renders a numbered chain (circle + label per
 * step, line between them). Visited completed steps are clickable so users
 * can jump back; the current step has the action accent; future steps are
 * disabled. Used by the Add Member wizard and the Describe-a-Job wizard.
 */

import styles from './Stepper.module.css';

export interface StepperStep {
  id: string;
  label: string;
}

export interface StepperProps<S extends StepperStep = StepperStep> {
  steps: S[];
  currentIdx: number;
  /** Ids of steps the user has reached. Clickable iff visited AND past. */
  visited: Set<string>;
  onJumpBack: (idx: number) => void;
}

export function Stepper<S extends StepperStep>({
  steps,
  currentIdx,
  visited,
  onJumpBack,
}: StepperProps<S>) {
  return (
    <ol className={styles.stepper} aria-label="Wizard progress">
      {steps.map((step, idx) => {
        const isCurrent = idx === currentIdx;
        const isCompleted = idx < currentIdx && visited.has(step.id);
        const isClickable = isCompleted;
        return (
          /* display:contents removes the <li> box from layout so the node and the
             connecting line become direct flex children of the <ol>. Keeps list
             semantics for screen readers while allowing a flat flex row layout. */
          <li key={step.id} style={{ display: 'contents' }}>
            <div className={styles.node}>
              <button
                type="button"
                className={[
                  styles.circle,
                  isCompleted ? styles.circleCompleted : '',
                  isCurrent ? styles.circleCurrent : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={isClickable ? () => onJumpBack(idx) : undefined}
                disabled={!isClickable}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={`Step ${idx + 1}: ${step.label}`}
              >
                {isCompleted ? '✓' : idx + 1}
              </button>
              <span
                className={[styles.label, isCurrent ? styles.labelCurrent : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 ? (
              <span
                className={[styles.line, isCompleted ? styles.lineCompleted : '']
                  .filter(Boolean)
                  .join(' ')}
                aria-hidden="true"
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
